import { GlobalContext, GlobalContextFunctions } from "#/api/global.context";
import { UserService } from "#/api/user.service";
import { PAGE } from "#/constants";
import { UserLevel } from "#/utils/jwt";
import {
  CreditCardOutlined,
  DashboardOutlined,
  KeyOutlined,
  LoginOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Drawer,
  Layout,
  Menu,
  message,
  notification,
  Typography,
} from "antd";
import { MenuInfo } from "rc-menu/lib/interface";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import LogoImg from "../../assets/logo.jpg";
import styles from "./styles.module.scss";

const { Content, Sider } = Layout;

enum MENU_KEYS {
  REGISTER = "REGISTER",
  MONTHLY = "MONTHLY",
  IMPERSONATE = "IMPERSONATE",
  ENCRYPT_PASSWORD = "ENCRYPT_PASSWORD",
  LOGOUT = "LOGOUT",
}

const MenuWapper: React.FC = ({ children }) => {
  const { state, dispatch } = useContext(GlobalContext);

  if (state.smallScreen) {
    return (
      <Fragment>
        {!state.menuVisible ? (
          <div style={{ position: "fixed", top: "10px", left: 0, zIndex: 1 }}>
            <Button
              icon={<MenuUnfoldOutlined />}
              type="primary"
              onClick={(_) => {
                dispatch({
                  type: GlobalContextFunctions.setMenuVisible,
                  menuVisible: true,
                });
              }}
            ></Button>
          </div>
        ) : null}
        <Drawer
          visible={state.menuVisible}
          placement="left"
          width={280}
          bodyStyle={{
            padding: 0,
            backgroundColor: "#00152a",
          }}
          closeIcon={null}
          onClose={() => {
            dispatch({
              type: GlobalContextFunctions.setMenuVisible,
              menuVisible: false,
            });
          }}
        >
          {children}
        </Drawer>
      </Fragment>
    );
  }

  return (
    <Sider
      width={280}
      style={{
        overflow: "auto",
        minHeight: "100vh",
        position: "fixed",
        left: 0,
      }}
    >
      {children}
    </Sider>
  );
};

interface MainLayoutProps {
  children: any;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { state, dispatch } = useContext(GlobalContext);
  const isLogged = !!state.session;
  const isAdmin = state.session?.level === UserLevel.Admin;

  const history = useHistory();
  const location = useLocation();

  const [selectedMenu, setSelectedMenu] = useState<string[]>(
    getSelectedKeys(location.pathname)
  );
  const [openKeys, setOpenKeys] = useState<string[]>(
    getSelectedKeys(location.pathname)
  );

  function getSelectedKeys(pathname: string) {
    function pathnameIn(paths: string[]) {
      return paths.some((i) => i === pathname);
    }

    const selected = [pathname];

    if (pathnameIn([PAGE.REGISTER_FORM(), PAGE.REGISTER_PASSWORD()])) {
      selected.push(MENU_KEYS.REGISTER);
    }

    if (pathnameIn([PAGE.MONTHLY_PAYMENT_HISTORY(), PAGE.PAY_MONTHLY()])) {
      selected.push(MENU_KEYS.MONTHLY);
    }
    return selected;
  }

  useEffect(() => {
    const keys = getSelectedKeys(location.pathname);
    setSelectedMenu(keys);
    setOpenKeys((current) => Array.from(new Set([...current, ...keys])));
  }, [location.pathname]);

  function logout() {
    dispatch({
      type: GlobalContextFunctions.setSession,
      session: undefined,
    });
    if (state.impersonatorSession) {
      dispatch({
        type: GlobalContextFunctions.setImpersonatorSession,
        impersonatorSession: undefined,
      });
    }
    history.push(PAGE.LOGIN());
  }

  async function handleImpersonate() {
    const username = window.prompt("Username do associado para impersonar:");
    if (!username) {
      return;
    }

    try {
      const session = await UserService.impersonate(username.trim());
      dispatch({
        type: GlobalContextFunctions.setImpersonatorSession,
        impersonatorSession: state.session,
      });
      dispatch({ type: GlobalContextFunctions.setSession, session });
      dispatch({ type: GlobalContextFunctions.setResetFeedback });
      dispatch({
        type: GlobalContextFunctions.setMenuVisible,
        menuVisible: false,
      });
      history.push(PAGE.MONTHLY_PAYMENT_HISTORY());
    } catch (error) {
      message.error("Não foi possível impersonar esse associado.", 5);
    }
  }

  async function handleEncryptPassword() {
    const password = window.prompt("Senha para gerar o hash:");
    if (!password) {
      return;
    }

    try {
      const encrypted = await UserService.encryptPassword(password);
      notification.success({
        message: "Hash gerado",
        description: (
          <Typography.Text copyable={{ text: encrypted }}>
            {encrypted}
          </Typography.Text>
        ),
        duration: 0,
      });
    } catch (error) {
      message.error("Não foi possível gerar o hash dessa senha.", 5);
    }
  }

  function handleReturnToAdmin() {
    const adminSession = state.impersonatorSession;
    if (!adminSession) {
      return;
    }

    dispatch({
      type: GlobalContextFunctions.setSession,
      session: adminSession,
    });
    dispatch({
      type: GlobalContextFunctions.setImpersonatorSession,
      impersonatorSession: undefined,
    });
    dispatch({ type: GlobalContextFunctions.setResetFeedback });
    dispatch({
      type: GlobalContextFunctions.setMenuVisible,
      menuVisible: false,
    });
    history.push(PAGE.ADMIN_DASHBOARD());
  }

  function handleClick(info: MenuInfo) {
    if (info.key === history.location.pathname) {
      window.location.reload();
    }

    switch (info.key) {
      case PAGE.LOGIN():
      case PAGE.SIGNUP():
      case PAGE.MONTHLY_PAYMENT_HISTORY():
      case PAGE.PAY_MONTHLY():
      case PAGE.REGISTER_FORM():
      case PAGE.REGISTER_PASSWORD():
      case PAGE.SPONTANEOUS_CONTRIBUTION():
      case PAGE.ADMIN_DASHBOARD():
        dispatch({ type: GlobalContextFunctions.setResetFeedback });
        dispatch({
          type: GlobalContextFunctions.setMenuVisible,
          menuVisible: false,
        });
        history.push(info.key);
        break;
      case MENU_KEYS.IMPERSONATE:
        handleImpersonate();
        break;
      case MENU_KEYS.ENCRYPT_PASSWORD:
        handleEncryptPassword();
        break;
      case MENU_KEYS.LOGOUT:
        logout();
        break;
      default:
        break;
    }
  }

  return (
    <Layout>
      <MenuWapper>
        <div className={styles["logo"]}>
          <img
            alt="Sociedade Beneficente Espírita Bezerra de Menezes - SBEBM"
            src={LogoImg}
          />
        </div>
        {state.impersonatorSession ? (
          <div style={{ padding: "12px 16px" }}>
            <Button
              block={true}
              danger={true}
              type="primary"
              icon={<UserSwitchOutlined />}
              onClick={handleReturnToAdmin}
            >
              Voltar para admin
            </Button>
          </div>
        ) : null}
        <Menu
          theme="dark"
          mode="inline"
          openKeys={openKeys}
          onOpenChange={(keys) => setOpenKeys(keys as string[])}
          selectedKeys={selectedMenu || []}
          onClick={handleClick}
        >
          {!isLogged ? (
            <>
              <Menu.Item key={PAGE.LOGIN()} icon={<LoginOutlined />}>
                Acessar portal do associado
              </Menu.Item>
              <Menu.Item key={PAGE.SIGNUP()} icon={<UserAddOutlined />}>
                Associe-se
              </Menu.Item>
            </>
          ) : null}
          {isLogged && isAdmin ? (
            <Menu.Item
              key={PAGE.ADMIN_DASHBOARD()}
              icon={<DashboardOutlined />}
            >
              Dashboard
            </Menu.Item>
          ) : null}
          {isLogged && isAdmin ? (
            <Menu.Item
              key={MENU_KEYS.IMPERSONATE}
              icon={<UserSwitchOutlined />}
            >
              Impersonar associado
            </Menu.Item>
          ) : null}
          {isLogged && isAdmin ? (
            <Menu.Item key={MENU_KEYS.ENCRYPT_PASSWORD} icon={<KeyOutlined />}>
              Gerar hash de senha
            </Menu.Item>
          ) : null}
          {isLogged && !isAdmin ? (
            <Menu.SubMenu
              key={MENU_KEYS.REGISTER}
              icon={<UserOutlined />}
              title="Cadastro"
            >
              <Menu.Item key={PAGE.REGISTER_FORM()}>Alterar cadastro</Menu.Item>
              <Menu.Item key={PAGE.REGISTER_PASSWORD()}>
                Alterar senha
              </Menu.Item>
            </Menu.SubMenu>
          ) : null}
          {isLogged && !isAdmin ? (
            <Menu.SubMenu
              key={MENU_KEYS.MONTHLY}
              icon={<CreditCardOutlined />}
              title="Mensalidades"
            >
              <Menu.Item key={PAGE.MONTHLY_PAYMENT_HISTORY()}>
                Histórico
              </Menu.Item>
              <Menu.Item key={PAGE.PAY_MONTHLY()}>Pagar mensalidade</Menu.Item>
            </Menu.SubMenu>
          ) : null}

          {!isAdmin ? (
            <Menu.Item
              key={PAGE.SPONTANEOUS_CONTRIBUTION()}
              icon={<CreditCardOutlined />}
            >
              Contribuição espontânea
            </Menu.Item>
          ) : null}

          {isLogged ? (
            <Menu.Item key={MENU_KEYS.LOGOUT} icon={<LogoutOutlined />}>
              Sair
            </Menu.Item>
          ) : null}
        </Menu>
      </MenuWapper>
      <Layout
        className="site-layout"
        style={{ marginLeft: state.smallScreen ? 0 : 280 }}
      >
        <Content
          style={{
            padding: state.smallScreen ? "36px 8px 8px" : "24px 16px",
            overflow: "initial",
            minHeight: "100vh",
          }}
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};
export default MainLayout;
